import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  SignalIcon,
  ClipboardDocumentListIcon,
  SpeakerWaveIcon,
  HeartIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import {
  hearingTestApi,
  questionnaireApi,
  tinnitusProfileApi,
  treatmentBookmarkApi,
} from '../services/api';
import {
  HearingTestResult,
  QuestionnaireResult,
  TinnitusProfile,
  TreatmentBookmark,
} from '../types';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (s: string) =>
  new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const formatFrequency = (hz: number) =>
  hz >= 1000 ? `${(hz / 1000).toFixed(1)} kHz` : `${hz} Hz`;

const thiSeverity = (score: number) => {
  if (score <= 16) return { label: 'Slight', bg: 'bg-green-100', text: 'text-green-800' };
  if (score <= 36) return { label: 'Mild', bg: 'bg-yellow-100', text: 'text-yellow-800' };
  if (score <= 56) return { label: 'Moderate', bg: 'bg-orange-100', text: 'text-orange-800' };
  if (score <= 76) return { label: 'Severe', bg: 'bg-red-100', text: 'text-red-800' };
  return { label: 'Catastrophic', bg: 'bg-red-200', text: 'text-red-900' };
};

const categoryLabel: Record<string, string> = {
  drugs: 'Medication',
  sound_therapy: 'Sound Therapy',
  physiotherapy: 'Physiotherapy',
  medical: 'Medical',
};

// ── Section wrapper ───────────────────────────────────────────────────────────

const Section: React.FC<{
  icon: React.ElementType;
  title: string;
  subtitle: string;
  action?: { label: string; to: string };
  children: React.ReactNode;
}> = ({ icon: Icon, title, subtitle, action, children }) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="w-9 h-9 bg-auris-teal bg-opacity-10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-auris-teal" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-auris-dark">{title}</h2>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      {action && (
        <Link
          to={action.to}
          className="text-sm text-auris-teal font-medium hover:underline"
        >
          {action.label} →
        </Link>
      )}
    </div>
    <div className="px-6 py-5">{children}</div>
  </section>
);

const EmptyState: React.FC<{ message: string; action: { label: string; to: string } }> = ({
  message,
  action,
}) => (
  <div className="text-center py-8">
    <p className="text-gray-500 text-sm mb-3">{message}</p>
    <Link
      to={action.to}
      className="inline-block bg-auris-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors"
    >
      {action.label}
    </Link>
  </div>
);

// ── Hearing Test section ──────────────────────────────────────────────────────

const HearingSection: React.FC<{ results: HearingTestResult[] }> = ({ results }) => {
  if (results.length === 0)
    return (
      <EmptyState
        message="No hearing tests recorded yet."
        action={{ label: 'Take Hearing Test', to: '/hearing-test' }}
      />
    );

  const latest = results[0];
  const frequencies = Object.keys(latest.left_ear_thresholds)
    .map(Number)
    .sort((a, b) => a - b);

  const chartData = {
    labels: frequencies.map(formatFrequency),
    datasets: [
      {
        label: 'Left Ear',
        data: frequencies.map((f) => latest.left_ear_thresholds[String(f)] ?? null),
        borderColor: '#EF4444',
        backgroundColor: '#EF4444',
        pointStyle: 'cross' as const,
        pointRadius: 7,
        tension: 0.1,
      },
      {
        label: 'Right Ear',
        data: frequencies.map((f) => latest.right_ear_thresholds[String(f)] ?? null),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        pointStyle: 'circle' as const,
        pointRadius: 5,
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) =>
            `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(0)}% volume`,
        },
      },
    },
    scales: {
      y: {
        reverse: true,
        min: 0,
        max: 1,
        title: { display: true, text: 'Threshold (lower = better hearing)' },
        ticks: { callback: (v: any) => `${(v * 100).toFixed(0)}%` },
      },
      x: { title: { display: true, text: 'Frequency' } },
    },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>Most recent test: {formatDate(latest.date)}</span>
        <span>{results.length} test{results.length !== 1 ? 's' : ''} recorded</span>
      </div>
      <div className="h-56">
        <Line data={chartData} options={chartOptions} />
      </div>
      {results.length > 1 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Previous tests</h3>
          <div className="space-y-1">
            {results.slice(1).map((r) => (
              <div
                key={r.id}
                className="flex justify-between text-sm text-gray-500 py-1 border-b border-gray-50"
              >
                <span>{formatDate(r.date)}</span>
                {r.test_duration_seconds && (
                  <span>{r.test_duration_seconds}s duration</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Questionnaire section ─────────────────────────────────────────────────────

const QuestionnaireSection: React.FC<{ results: QuestionnaireResult[] }> = ({ results }) => {
  if (results.length === 0)
    return (
      <EmptyState
        message="No THI questionnaires completed yet."
        action={{ label: 'Take Questionnaire', to: '/questionnaire' }}
      />
    );

  const sorted = [...results].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  const latest = sorted[sorted.length - 1];
  const severity = thiSeverity(latest.total_score);

  const chartData = {
    labels: sorted.map((r) => formatDate(r.date)),
    datasets: [
      {
        label: 'THI Score',
        data: sorted.map((r) => r.total_score),
        borderColor: '#6FB1A0',
        backgroundColor: '#6FB1A0',
        tension: 0.2,
        pointRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { min: 0, max: 100, title: { display: true, text: 'THI Score' } },
      x: { title: { display: true, text: 'Date' } },
    },
  };

  return (
    <div className="space-y-4">
      {/* Latest score card */}
      <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <div className="text-3xl font-bold text-auris-dark">{latest.total_score}</div>
          <div className="text-xs text-gray-500">out of 100</div>
        </div>
        <div>
          <span className={`px-2 py-1 rounded text-xs font-medium ${severity.bg} ${severity.text}`}>
            {severity.label}
          </span>
          <div className="text-xs text-gray-500 mt-1">{formatDate(latest.date)}</div>
        </div>
      </div>

      {sorted.length > 1 && (
        <div className="h-48">
          <Line data={chartData} options={chartOptions} />
        </div>
      )}

      {/* History list */}
      <div className="space-y-1">
        {[...sorted].reverse().slice(1).map((r) => {
          const s = thiSeverity(r.total_score);
          return (
            <div
              key={r.id}
              className="flex justify-between items-center text-sm py-1 border-b border-gray-50"
            >
              <span className="text-gray-500">{formatDate(r.date)}</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-auris-dark">{r.total_score}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${s.bg} ${s.text}`}>{s.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Self-Assessment section ───────────────────────────────────────────────────

const AssessmentSection: React.FC<{ profiles: TinnitusProfile[] }> = ({ profiles }) => {
  if (profiles.length === 0)
    return (
      <EmptyState
        message="No tinnitus assessments recorded yet."
        action={{ label: 'Start Assessment', to: '/assessment' }}
      />
    );

  const sorted = [...profiles].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  const latest = sorted[sorted.length - 1];

  const freqChartData = {
    labels: sorted.map((p) => formatDate(p.timestamp)),
    datasets: [
      {
        label: 'Frequency (Hz)',
        data: sorted.map((p) => p.frequency_hz),
        borderColor: '#AEC6CF',
        backgroundColor: '#AEC6CF',
        tension: 0.2,
        pointRadius: 3,
      },
    ],
  };

  const volChartData = {
    labels: sorted.map((p) => formatDate(p.timestamp)),
    datasets: [
      {
        label: 'Volume (dB)',
        data: sorted.map((p) =>
          p.volume_db ?? Math.round(20 * Math.log10(p.volume_normalized * 100)),
        ),
        borderColor: '#F5A623',
        backgroundColor: '#F5A623',
        tension: 0.2,
        pointRadius: 3,
      },
    ],
  };

  const miniOptions = (yLabel: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { title: { display: true, text: yLabel } },
      x: { ticks: { maxTicksLimit: 5 } },
    },
  });

  return (
    <div className="space-y-4">
      {/* Latest values */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500 mb-1">Latest Frequency</div>
          <div className="text-2xl font-bold text-auris-blue">
            {formatFrequency(latest.frequency_hz)}
          </div>
          <div className="text-xs text-gray-400 mt-1">{formatDate(latest.timestamp)}</div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500 mb-1">Latest Volume</div>
          <div className="text-2xl font-bold text-auris-teal">
            {latest.volume_db
              ? `${latest.volume_db.toFixed(1)} dB`
              : `${Math.round(20 * Math.log10(latest.volume_normalized * 100))} dB`}
          </div>
          <div className="text-xs text-gray-400 mt-1">{latest.method} method</div>
        </div>
      </div>

      {sorted.length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-40">
            <Line data={freqChartData} options={miniOptions('Hz')} />
          </div>
          <div className="h-40">
            <Line data={volChartData} options={miniOptions('dB')} />
          </div>
        </div>
      )}

      <div className="text-xs text-gray-400 text-right">
        {profiles.length} measurement{profiles.length !== 1 ? 's' : ''} recorded
      </div>
    </div>
  );
};

// ── Treatments section ────────────────────────────────────────────────────────

const TreatmentsSection: React.FC<{ bookmarks: TreatmentBookmark[] }> = ({ bookmarks }) => {
  if (bookmarks.length === 0)
    return (
      <EmptyState
        message="No treatments bookmarked yet."
        action={{ label: 'Browse Treatments', to: '/treatments' }}
      />
    );

  const byCategory = bookmarks.reduce<Record<string, TreatmentBookmark[]>>((acc, b) => {
    const cat = categoryLabel[b.category] ?? b.category;
    return { ...acc, [cat]: [...(acc[cat] ?? []), b] };
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat}>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            {cat}
          </h3>
          <div className="space-y-2">
            {items.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <HeartIcon className="w-4 h-4 text-auris-teal flex-shrink-0" />
                  <span className="text-sm text-auris-dark font-medium">{b.treatment_name}</span>
                </div>
                <span className="text-xs text-gray-400">{formatDate(b.bookmarked_at)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-400 text-right">
        {bookmarks.length} treatment{bookmarks.length !== 1 ? 's' : ''} bookmarked
      </p>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const MyResults: React.FC = () => {
  const { user } = useUser();
  const { showError } = useToast();

  const [hearingResults, setHearingResults] = useState<HearingTestResult[]>([]);
  const [questionnaireResults, setQuestionnaireResults] = useState<QuestionnaireResult[]>([]);
  const [tinnitusProfiles, setTinnitusProfiles] = useState<TinnitusProfile[]>([]);
  const [bookmarks, setBookmarks] = useState<TreatmentBookmark[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    Promise.all([
      hearingTestApi.getMyResults(),
      questionnaireApi.getMyResults(),
      tinnitusProfileApi.getMyProfiles(),
      treatmentBookmarkApi.getMyBookmarks(),
    ])
      .then(([hearing, questionnaire, tinnitus, bmarks]) => {
        setHearingResults(hearing);
        setQuestionnaireResults(questionnaire);
        setTinnitusProfiles(tinnitus);
        setBookmarks(bmarks);
      })
      .catch(() => showError('Load failed', 'Could not load your results. Please try again.'))
      .finally(() => setLoading(false));
  }, [user, showError]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-auris-dark mb-2">Sign in to see your results</h2>
        <p className="text-gray-500 text-sm">
          Your hearing test, questionnaire scores, assessments, and bookmarked treatments will
          appear here once you sign in.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="animate-pulse space-y-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-auris-dark mb-2">My Results</h1>
        <p className="text-gray-500 text-sm">
          All your recorded data in one place — hearing tests, questionnaire scores,
          tinnitus measurements, and bookmarked treatments.
        </p>
      </div>

      <div className="space-y-6">
        <Section
          icon={SignalIcon}
          title="Hearing Test"
          subtitle="Audiogram — frequency thresholds per ear"
          action={{ label: 'New test', to: '/hearing-test' }}
        >
          <HearingSection results={hearingResults} />
        </Section>

        <Section
          icon={ClipboardDocumentListIcon}
          title="THI Questionnaire"
          subtitle="Tinnitus Handicap Inventory score over time"
          action={{ label: 'New questionnaire', to: '/questionnaire' }}
        >
          <QuestionnaireSection results={questionnaireResults} />
        </Section>

        <Section
          icon={SpeakerWaveIcon}
          title="Tinnitus Self-Assessment"
          subtitle="Frequency and volume measurements over time"
          action={{ label: 'New assessment', to: '/assessment' }}
        >
          <AssessmentSection profiles={tinnitusProfiles} />
        </Section>

        <Section
          icon={HeartIcon}
          title="Treatment Records"
          subtitle="Treatments you have bookmarked"
          action={{ label: 'Browse treatments', to: '/treatments' }}
        >
          <TreatmentsSection bookmarks={bookmarks} />
        </Section>
      </div>
    </div>
  );
};

export default MyResults;
